import _ from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import { CASINO_GAMES_LOAD_MORE_CAROUSEL, CASINO_GAMES_PER_CAROUSEL } from '@/config/casino'
import { useGetCasinoGamesQuery } from '@/store/api/casinoApi'
import type { CasinoGame, CasinoGamesRequest } from '@/types/casino'

export function useLobbyCasinoGames(request: Omit<CasinoGamesRequest, 'page' | 'per_page'>) {
	const [games, setGames] = useState<CasinoGame[]>([])
	const [total, setTotal] = useState(0)
	const [page, setPage] = useState(1)
	const [perPage, setPerPage] = useState(CASINO_GAMES_PER_CAROUSEL)
	const requestKey = JSON.stringify(request)

	useEffect(() => {
		setPage(1)
		setPerPage(CASINO_GAMES_PER_CAROUSEL)
		setGames([])
		setTotal(0)
	}, [requestKey])

	const { data, isLoading, isError, isFetching } = useGetCasinoGamesQuery({
		...request,
		page,
		per_page: perPage,
	})

	useEffect(() => {
		if (!data) {
			return
		}

		setTotal(data.total)

		if (page === 1) {
			setGames(data.data)
			return
		}

		setGames((previousGames) => {
			const existingIds = new Set(_.map(previousGames, 'id'))
			const appendedGames = _.filter(data.data, (game) => !existingIds.has(game.id))

			return [...previousGames, ...appendedGames]
		})
	}, [data, page])

	const hasMoreGames = total > CASINO_GAMES_PER_CAROUSEL && games.length < total

	const loadMore = useCallback(() => {
		if (!hasMoreGames || isFetching) {
			return
		}

		setPerPage(CASINO_GAMES_LOAD_MORE_CAROUSEL)
		setPage(Math.floor(games.length / CASINO_GAMES_LOAD_MORE_CAROUSEL) + 1)
	}, [games.length, hasMoreGames, isFetching])

	return {
		games,
		total,
		isLoading: isLoading && page === 1,
		isLoadingMore: isFetching && page > 1,
		isError,
		hasMoreGames,
		loadMore,
	}
}
